import LazyLoader from 'Components/LazyLoader';
import { ChatHistory } from 'Components/VideoComponent/VideoMenu/VideoHistory/VideoHistoryDetails/ChatHistory';
import { usePreferences } from 'Hooks/preferences/usePreferences';
import { observer } from 'mobx-react';
import Softphone from 'Modules/softphone/components/legacy/Softphone';
import { SoftphoneUI } from 'Modules/softphone/components/SoftphoneUI';
import { useUIStore } from 'Modules/ui/index.store';
import { VideoSummarySidePanel } from 'Modules/videoHistory/components/VideoSummarySidePanel';
import React, { useCallback, useEffect } from 'react';
import { Sidebar, useSidebar } from '~/lib/ui/sidebar';
import { EntityProfileV2 } from '../EntityProfileV2';
import usePanelOrder from './hooks/usePanelOrder';

/**
 * Softphone Coordination Render Legacy/New
 */
const SoftphoneRenderElement = ({
  isNewSoftphoneEnabled,
  panelOrder,
}: {
  isNewSoftphoneEnabled?: boolean;
  panelOrder: string[];
}) => {
  const {
    preferences: { floatingSoftphone: isSoftphoneFixed },
  } = usePreferences();

  const isFirstPanel = panelOrder[0] === 'call' || panelOrder[0] === 'dialpad';
  const isVisible =
    panelOrder.includes('call') || panelOrder.includes('dialpad');

  if (!isVisible) {
    return null;
  }

  if (isNewSoftphoneEnabled) {
    return (
      <div
        style={{ display: isFirstPanel ? 'block' : 'none' }}
        className={isSoftphoneFixed ? 'overflow-y-auto scroll-area-content h-full' : ''}
      >
        <SoftphoneUI variant={isSoftphoneFixed ? 'fixed' : 'popup'} />
      </div>
    );
  }

  return <Softphone hidden={!isFirstPanel} />;
};

const SidePanelManager = ({
  isNewSoftphoneEnabled,
}: {
  isNewSoftphoneEnabled?: boolean;
}) => {
  const { setOpen } = useSidebar();

  const { entitySidePanelInfo, sidePanelCustomWidth, setSidePanelCustomWidth } =
    useUIStore();

  const panelOrder = usePanelOrder({ isNewSoftphoneEnabled });

  /**
   * Opens the side panel when there's any panel open.
   * Closes the side panel when there's no panel open.
   */
  useEffect(() => {
    if (panelOrder.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [panelOrder, setOpen]);

  /**
   * Sets the side panel width based on the panel to display.
   */
  useEffect(() => {
    setSidePanelCustomWidth(
      panelOrder[0] === 'historyDetails' || panelOrder[0] === 'videoSummary'
        ? '381px'
        : null
    );
  }, [panelOrder, setSidePanelCustomWidth]);

  /**
   * Shows the panel at the first position in the panel order.
   * Also renders the other panels that are in the panel order, but hidden (so the state is preserved and the user can navigate back to them)
   */
  const renderPanels = useCallback(() => {
    return (
      <>
        <SoftphoneRenderElement
          isNewSoftphoneEnabled={isNewSoftphoneEnabled}
          panelOrder={panelOrder}
        />

        {panelOrder[0] === 'entityOrContactLoading' ? (
          <div className={'flex justify-center items-center h-full'}>
            <LazyLoader delay={0} isLoading />
          </div>
        ) : null}

        {panelOrder.includes('entity') ? (
          <EntityProfileV2
            key={entitySidePanelInfo?.entityId}
            hidden={panelOrder[0] !== 'entity'}
          />
        ) : null}

        {panelOrder.includes('historyDetails') ? (
          <ChatHistory hidden={panelOrder[0] !== 'historyDetails'} />
        ) : null}

        {panelOrder.includes('videoSummary') ? (
          <VideoSummarySidePanel hidden={panelOrder[0] !== 'videoSummary'} />
        ) : null}
      </>
    );
  }, [entitySidePanelInfo, panelOrder, isNewSoftphoneEnabled]);

  return (
    <Sidebar
      variant="sidebar"
      collapsible="offcanvas"
      side="right"
      className="bg-white max-h-[calc(100vh-70px)] h-full top-[70px] bottom-0"
      width={sidePanelCustomWidth}
    >
      {renderPanels()}
    </Sidebar>
  );
};

export default observer(SidePanelManager);
